/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";


// @material-ui core components
import Card from "@mui/material/Card";
import FeaturesCard from "examples/Cards/FeaturesCard";
// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import LockIcon from "@mui/icons-material/Lock";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import ReactPlayer from "react-player";
import LinkedIn from '@mui/icons-material/LinkedIn';
import Twitter from '@mui/icons-material/Twitter';
import LinkIcon from '@mui/icons-material/Link';
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "context";
import MDTypography from "components/MDTypography";
import {
    Divider,
} from "@mui/material";
import MDEditor from "@uiw/react-md-editor";

// Material Dashboard 2 PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

// Settings page components
import CourseHome from "layouts/pages/course-content/components/CourseHome";
import Assessment from "layouts/pages/course-content/components/Assessment";
import CourseModule from "layouts/pages/course-content/components/CourseModule";

// context
import { useMaterialUIController, setOpenChatBot } from "context";
import crudsService from "services/cruds-service";
import PublicDashboardLayout from "examples/LayoutContainers/PublicDashboardLayout";
import PublicDashboardNavbar from "examples/Navbars/PublicDashboardNavbar";

function PublicCourseContent() {
    const [activeStep, setActiveStep] = useState(0);
    const [controller, dispatch] = useMaterialUIController();
    const { openChatBot } = controller;
    const [tabsOrientation, setTabsOrientation] = useState("horizontal");
    const [tabValue, setTabValue] = useState(0);
    const handleSetTabValue = (event, newValue) => setTabValue(newValue);
    const navigate = useNavigate();
    const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);
    const [user, setUser] = useState("");
    // get the course id from the url
    const location = useLocation();
    const courseId = location.pathname.split("/").pop();
    const [content, setActiveContent] = useState(<></>);
    const [baseRedirect, setBaseRedirect] = useState("/course-content/");
    const [loading, setLoading] = useState({ loading: true, message: "Loading Course Information..." });

    const [courseInformation, setCourseInformation] = useState({
        app_name: "",
        short_description: "",
        podcastLink: "",
        previewVideoLink: "",
        previewSlideDeckLink: "",
        app_image_location: "",
        course_names_slides: [],
        course_names_videos: [],
        has_chatbot: false,
        has_quiz: false,
        codelabs: [],
        modules: [],
        payment_link: "",
    });

    useEffect(() => {
        async function getCourseInformation(course_id) {
            const timeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Request timed out")), 10000)
            );
            try {
                const response = await Promise.race([
                    crudsService.getPublicCourseInfo(course_id),
                    timeout,
                ]);
                setCourseInformation(response);
            } catch (error) {
                console.error("Failed to load course information:", error);
            } finally {
                setLoading({ loading: false, message: "" });
            }
        }

        async function checkToken() {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    setUser("");
                    return;
                }

                let user_id = await getCurrentUser();

                if (!user_id) {
                    setIsAuthenticated(false);
                    localStorage.removeItem("token");
                    return;
                }

                setUser(user_id);
                setIsAuthenticated(true);
                navigate(`/course-content/${courseId}`);
            } catch (error) {
                console.error("Error in checkToken:", error);
            }
        }

        // returns the course summary page introduction markdown string
        getCourseInformation(courseId);
        checkToken();
    }, [navigate, courseId]);

    useEffect(() => {
        const handleClick = (event) => {
            // Prevent login trigger if clicking inside an IconButton
            if (event.target.closest(".exclude-click")) {
                return;
            }

            handleLogin();
        };

        window.addEventListener("click", handleClick);

        return () => {
            window.removeEventListener("click", handleClick);
        };
    }, []);


    const handleLogin = () => {
        let redirectTo = window.location.pathname; // Get current path

        // Transform URL if it matches "/course-information/:id"
        const match = redirectTo.match(/^\/course-information\/(.+)$/);
        if (match) {
            redirectTo = `/course-content/${match[1]}`;
        }

        if (user && user.trim() !== "") {
            navigate(redirectTo);
        } else {
            navigate(`/auth/login?redirect=${encodeURIComponent(redirectTo)}`);
        }
    };


    const [courseCompletionStatus, setCourseCompletionStatus] = useState(false);
    const [coursePurchaseStatus, setCoursePurchaseStatus] = useState(false);

    // Change the openChatBot state
    const handleChatBotOpen = () => setOpenChatBot(dispatch, !openChatBot);

    useEffect(() => {
        // A function that sets the orientation state of the tabs.
        function handleTabsOrientation() {
            return window.innerWidth < breakpoints.values.sm
                ? setTabsOrientation("vertical")
                : setTabsOrientation("horizontal");
        }

        /** 
         The event listener that's calling the handleTabsOrientation function when resizing the window.
        */
        window.addEventListener("resize", handleTabsOrientation);

        // Call the handleTabsOrientation function to set the state with the initial value.
        handleTabsOrientation();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleTabsOrientation);
    }, [tabsOrientation]);

    const chatBotButton = courseInformation.has_chatbot ? (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3.25rem"
            height="3.25rem"
            bgColor="info"
            shadow="sm"
            borderRadius="50% 50% 0% 50%"
            position="fixed"
            right="2rem"
            bottom="2rem"
            zIndex={99}
            color="white"
            sx={{ cursor: "pointer" }}
            onClick={handleChatBotOpen}
        >
            <Icon fontSize="small" color="inherit">
                smart_toy
            </Icon>
        </MDBox>
    ) : (
        <></>
    );

    const getStepContent = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                setActiveContent(
                    <CourseHome
                        courseCompletionStatus={courseCompletionStatus}
                        coursePurchaseStatus={coursePurchaseStatus}
                        courseId={courseId}
                        switchStep={() => {
                            handleSetTabValue(null, 1);
                            setActiveContent(
                                <CourseModule
                                    courseName={courseInformation.app_name}
                                    courseCompletionStatus={courseCompletionStatus}
                                    coursePurchaseStatus={coursePurchaseStatus}
                                    courseId={courseId}
                                    modules={courseInformation.modules}
                                    isPreviewMode={true}
                                    paymentLink={courseInformation.payment_link}
                                />
                            );
                        }}
                    />
                );
                break;
            case 1:
                setActiveContent(
                    <CourseModule
                        courseName={courseInformation.app_name}
                        courseCompletionStatus={courseCompletionStatus}
                        coursePurchaseStatus={coursePurchaseStatus}
                        courseId={courseId}
                        modules={courseInformation.modules}
                        isPreviewMode={true}
                        paymentLink={courseInformation.payment_link}
                    />
                );
                break;
            case 2:
                setActiveContent(
                    <Assessment
                        courseName={courseInformation.app_name}
                        courseCompletionStatus={courseCompletionStatus}
                        coursePurchaseStatus={coursePurchaseStatus}
                        courseId={courseId}
                    />
                );
                break;
            case 3:
                setActiveContent(
                    // <CourseFeedback
                    //     courseName={courseInformation.app_name}
                    //     courseId={courseId}
                    // />
                );
                break;
        }
    };
    const handleShareCourse = (socialMediaPlatform) => {
        // handleShareDialogClose();
        let currentUrl = window.location.href;
        // currentUrl = currentUrl.replace('course-content', 'course-information');

        if (socialMediaPlatform === "twitter") {
            const tweetText = `Level up your skills with the course: %0A${courseInformation.app_name}! %0A%0ACheck it out now:`;
            const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(currentUrl)}`;

            window.open(twitterUrl, "_blank");
        }
        else if (socialMediaPlatform === "linkedin") {
            const linkedInText = `Level up your skills with the course: ${courseInformation.app_name}!%0A %0AHere is a short description about the course: %0A${courseInformation.short_description}.%0A%0ACheck it out now: %0A${currentUrl}`;
            // Replace 'course-content' with 'course-information'
            const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${linkedInText}&url=${encodeURIComponent(currentUrl)}`;

            window.open(linkedInUrl, "_blank");
        }
    };


    const handleCopyUrl = () => {
        // handleShareDialogClose();
        let currentUrl = window.location.href;
        navigator.clipboard.writeText(currentUrl)
            .then(() => {
                alert("URL copied to clipboard!");
            })
            .catch((err) => {
                console.error("Failed to copy URL: ", err);
            });
    };

    return (
        <PublicDashboardLayout>
            <PublicDashboardNavbar user={user} />
            {loading.loading ? (
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="60vh"
                >
                    <CircularProgress color="primary" />
                    <MDTypography variant="h6" style={{ marginLeft: 16 }}>
                        {loading.message}
                    </MDTypography>
                </MDBox>
            ) : (
                <MDBox mt={1}>
                    <MDBox mb={3}>
                        <AppBar position="static">
                            <Tabs
                                orientation={tabsOrientation}
                                value={tabValue}
                                onChange={handleSetTabValue}
                            >
                                <Tab
                                    icon={<Icon>home</Icon>}
                                    label="Summary"
                                    onClick={() => getStepContent(0)}
                                />
                                <Tab
                                    icon={<Icon>slideshow</Icon>}
                                    label={coursePurchaseStatus ? "Modules" : "Modules (Preview)"}
                                    onClick={() => getStepContent(1)}
                                />
                                {courseInformation.has_quiz && (
                                    <Tab
                                        icon={<Icon>quiz</Icon>}
                                        label={
                                            coursePurchaseStatus ? (
                                                "Assessment"
                                            ) : (
                                                <>
                                                    Assessment <LockIcon />
                                                </>
                                            )
                                        }
                                        disabled={!coursePurchaseStatus}
                                        onClick={() => getStepContent(2)}
                                    />
                                )}
                                <Tab
                                    icon={<Icon>feedback</Icon>}
                                    label="Feedback"
                                    onClick={() => getStepContent(3)}
                                />
                            </Tabs>
                        </AppBar>
                    </MDBox>

                    <Card id="basic-info" sx={{ overflow: "visible" }}>
                        {tabValue === 1 ? (
                            <MDBox p={2}>{content}</MDBox>
                        ) : null}
                        <MDBox style={{ display: tabValue === 0 ? "block" : "none" }}>
                            <Grid container spacing={4} p={4} pb={0}>
                                <Grid
                                    item
                                    xs={12}
                                    lg={8}
                                    pr={4}
                                    sx={{
                                        position: "relative", // Required for pseudo-element
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            width: "1px", // Base width
                                            height: "100%",
                                            background: "linear-gradient(to bottom, transparent, #e0e0e0 50%, transparent)",
                                            opacity: 5, // Ensures opacity is applied
                                        },
                                    }}>
                                    <MDTypography variant="h2" mr={2}>
                                        {courseInformation.app_name}
                                    </MDTypography>
                                    {/* Short Description */}
                                    <div data-color-mode="light">
                                        <MDEditor.Markdown
                                            source={courseInformation.short_description}
                                            style={{ color: "#344767" }}
                                            components={{
                                                p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
                                                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                                            }}
                                        />
                                    </div>

                                    {/* Course Introduction */}
                                    <div data-color-mode="light" style={{ marginTop: "16px" }}>
                                        <MDEditor.Markdown
                                            source={courseInformation.home_page_introduction}
                                            style={{ color: "#344767" }}
                                            components={{
                                                p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
                                                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                                            }}
                                        />
                                    </div>
                                </Grid>
                                {/* Right Content: Image, Course Features, and Course Preview */}
                                <Grid item xs={12} lg={4} pl={4}>
                                    <MDBox
                                        component="img"
                                        src={courseInformation.app_image_location}
                                        alt={courseInformation.app_name}
                                        borderRadius="lg"
                                        shadow="md"
                                        width="100%"
                                        mb={3}
                                    />

                                    {courseInformation?.podcastLink?.trim() && (
                                        <>
                                            {/* Course Features */}
                                            <MDTypography variant="h4" my={2}>
                                                Course Audio Guide
                                            </MDTypography>
                                            <audio controls style={{ display: "block", width: "100%" }}>
                                                <source src={courseInformation.podcastLink} type="audio/mpeg" />
                                                Your browser does not support the audio element.
                                            </audio>
                                        </>

                                    )}

                                    {/* Course Features */}
                                    {/* <MDTypography variant="h4" my={2}>
                                        Course Features
                                    </MDTypography> */}

                                    {/* <MDBox pt={2}>
                                        <FeaturesCard
                                            color="info"
                                            icon="view_module"
                                            title="Course Modules"
                                            count={courseInformation.course_names_videos.length}
                                        />
                                    </MDBox> */}
                                    {/* Course Preview */}
                                    {courseInformation?.previewVideoLink && courseInformation?.previewSlideDeckLink && (
                                        <>
                                            <MDTypography variant="h4" my={2}>
                                                Course Preview
                                            </MDTypography>
                                            <Card
                                                style={{
                                                    padding: "20px",
                                                    borderTop: "2px solid #f5f5f5",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {/* Video Player - Fully Contained */}
                                                <div style={{ width: "100%", position: "relative", aspectRatio: "16 / 9" }}>
                                                    <ReactPlayer
                                                        url={courseInformation.previewVideoLink}
                                                        controls
                                                        width="100%"
                                                        height="100%"
                                                        style={{ position: "absolute", top: 0, left: 0 }}
                                                        config={{ file: { attributes: { controlsList: "nodownload" } } }}
                                                    />
                                                </div>

                                                <Divider style={{ margin: "30px 0", opacity: 0.5, width: "100%" }} />

                                                {/* Slide Deck - Fully Contained */}
                                                <div style={{ width: "100%", position: "relative" }}>
                                                    <iframe
                                                        src={courseInformation.previewSlideDeckLink}
                                                        title="PDF Document"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            minHeight: "350px",
                                                            border: "none",
                                                        }}
                                                    />
                                                </div>
                                            </Card>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            <Divider style={{ opacity: 5 }} />

                            <Grid container px={4} mb={4}>
                                <Grid item xs={12} lg={4} p={1}>
                                    <MDButton
                                        color="info"
                                        variant="outlined"
                                        fullWidth
                                        onClick={handleLogin}
                                        className="register-button"
                                    >
                                        Register
                                    </MDButton>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        className="exclude-click"
                                        color="info"
                                        onClick={() => handleShareCourse("linkedin")}
                                        aria-label="Share on LinkedIn"
                                    >
                                        <LinkedIn fontSize="large" />
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        className="exclude-click"
                                        color="info"
                                        onClick={() => handleShareCourse("twitter")}
                                        aria-label="Share on Twitter"
                                    >
                                        <Twitter fontSize="large" />
                                    </IconButton>
                                </Grid>
                                <Grid item >
                                    <IconButton
                                        className="exclude-click"
                                        color="info"
                                        onClick={handleCopyUrl}
                                        aria-label="Copy URL"
                                    >
                                        <LinkIcon fontSize="large" color="info" /> {/* Small icon */}
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </MDBox>
                    </Card>
                </MDBox>
            )
            }

        </PublicDashboardLayout >
    );


}

export default PublicCourseContent;
